import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ListTree, Pencil, Plus, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { useLabel } from '@/lib/labels';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * `/settings/leave-types` (v0.4 休暇管理 TD-26、設計 §6-1 L-3)。
 * 休暇種別マスタの CRUD / 法定義務の未登録警告 (D-276) / 法定値の確認 (D-279) / 慶弔の事由×続柄。
 *
 * ⚠️ §6-3⑤ (D-279): 法定値の未確認は隠さない。0件でも「すべて確認済み」を恒常表示する。
 * ⚠️ 法定行 (`statutory_kind` あり) は `tenant_overridable_fields` に無い列をサーバーが
 *    カタログ値で強制する (`resolveLeaveTypeLockedFields`)。編集しても無駄になる欄は
 *    入力させず「法定で固定」と表示する。
 * ⚠️ `is_mandatory` な法定行は statutory_kind の変更も削除も拒否されるため削除ボタンを出さない (D-276)。
 * ⚠️ §6-3② (D-269) との関係: 本画面は種別マスタのみを扱い、年休の承認/却下 UI は一切持たない。
 */

// ⚠️ フォーカスリングに brand-red を使わない (ui-guidelines §2.2/§7.2: 赤はエラーと誤読される)。
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus:border-chrome-base focus:outline-none';
// 数値入力のネイティブスピナーは抑制する (AttendanceSettingsPage 等で確立済みのパターン)
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;
const NO_STATUTORY_VALUE = '__none__';
const NO_APPROVAL_VALUE = '__none__';
const NO_RELATIONSHIP_VALUE = '__any__';

// -----------------------------------------------------------------------------
// 型 (apps/api/src/routes/leave-types.ts の serializeLeaveType / catalog / legal-values に対応)
// ⚠️ minimum_rule_findings だけは lib/leave/minimum-rules.ts の戻り値をそのまま返しているため
//    camelCase (constantKey / sourceCitation / lastVerifiedOn)。他は snake_case。
// -----------------------------------------------------------------------------
type MinimumRuleFinding =
  | { kind: 'unconfirmed'; field: string; constantKey: string }
  | {
      kind: 'below_minimum'; field: string; constantKey: string;
      actual: number | null; floor: number; sourceCitation: string; lastVerifiedOn: string;
    };

type LeaveType = {
  leave_type_id: string; code: string; label: string; statutory_kind: string | null;
  day_treatment: string; balance_model: string; allowed_units: string[];
  accrual_logic: string; pay_treatment: string; pay_calc_method: string | null;
  expiry_model: string; expiry_years: number | null;
  attendance_rate_treatment: string; sensitivity_level: string;
  approval_usage_point_id: string | null;
  requires_family_target: boolean; is_period_type: boolean;
  annual_cap_days: number | null; annual_cap_days_multi: number | null;
  annual_cap_period_basis: string | null; per_event_cap_days: number | null;
  per_target_cap_days: number | null; per_target_max_splits: number | null;
  hourly_annual_cap_days: number | null; tenure_rules_json: string | null;
  carryover_source_leave_type_id: string | null; carryover_max_days: number | null;
  requires_event_code: boolean; is_active: boolean; source: string;
  sort_order: number; effective_from: string; effective_to: string | null;
  minimum_rule_findings: MinimumRuleFinding[];
};

type CatalogEntry = {
  statutory_kind: string; label: string; balance_model: string; allowed_units: string[];
  accrual_logic: string; default_pay_treatment: string; expiry_model: string;
  sensitivity_level: string; attendance_rate_treatment: string;
  approval_default_mode: string; work_prohibition: string;
  requires_family_target: boolean; is_period_type: boolean; is_mandatory: boolean;
  tenant_overridable_fields: string[]; statute_citation: string; is_registered: boolean;
};
type CatalogResponse = { catalog: CatalogEntry[]; missing_mandatory_kinds: string[] };

type LegalValue = {
  constant_key: string; value: unknown; unit: string | null; label: string; explanation: string;
  source_citation: string; last_verified_on: string;
  status: 'confirmed' | 'unconfirmed' | 'stale';
  confirmed_at: string | null; confirmed_by_user_id: string | null;
};

type EventRule = {
  rule_id: string; event_code: string; event_label: string; relationship_code: string | null;
  granted_days: number; is_paid: boolean | null; sort_order: number;
};
type FamilyRelationship = { relationship_code: string; label: string; kinship_degree: number | null; is_care_target: boolean; is_child: boolean };
type ApprovalUsagePoint = { usage_point_id: string; label: string; workflow_type: string; default_mode: string; is_implemented: boolean };

// -----------------------------------------------------------------------------
// 選択肢 (apps/api/src/lib/leave-constants.ts の zod enum と 1:1)
// -----------------------------------------------------------------------------
const BALANCE_MODELS = [
  { value: 'granted', label: '付与制 (付与した日数から消化)' },
  { value: 'annual_cap', label: '年間上限制' },
  { value: 'per_event_cap', label: '事由ごと上限制' },
  { value: 'per_target_cap', label: '対象者ごと上限制' },
  { value: 'unlimited', label: '無制限' },
  { value: 'fixed_calendar', label: '会社が期間を決定' },
] as const;
const UNITS = [
  { value: 'day', label: '日' }, { value: 'half_day', label: '半日' }, { value: 'hour', label: '時間' },
] as const;
const ACCRUAL_LOGICS = [
  { value: 'statutory_table', label: '法定の比例付与表' },
  { value: 'fixed_annual', label: '毎年一定日数' },
  { value: 'tenure_based', label: '勤続年数に応じる' },
  { value: 'on_event', label: '事由の発生時' },
  { value: 'none', label: '付与しない' },
] as const;
const PAY_TREATMENTS = [
  { value: 'paid_regular', label: '有給 (通常賃金)' },
  { value: 'paid_average', label: '有給 (平均賃金)' },
  { value: 'paid_standard_remuneration', label: '有給 (標準報酬日額)' },
  { value: 'unpaid', label: '無給' },
  { value: 'partial', label: '一部支給' },
] as const;
const PAY_CALC_METHODS = [
  { value: 'regular', label: '通常賃金' }, { value: 'average', label: '平均賃金' },
  { value: 'standard_remuneration', label: '標準報酬日額' },
] as const;
const EXPIRY_MODELS = [
  { value: 'carryover_with_expiry', label: '繰越あり (時効あり)' },
  { value: 'expire_at_year_end', label: '年度末に消滅' },
  { value: 'no_expiry', label: '時効なし' },
  { value: 'expire_on_event_end', label: '事由の終了で消滅' },
] as const;
const ATTENDANCE_TREATMENTS = [
  { value: 'counted_as_attended', label: '出勤したものとみなす' },
  { value: 'excluded_from_denominator', label: '出勤率の分母から除く' },
  { value: 'counted_as_absent', label: '欠勤として扱う' },
] as const;
const SENSITIVITY_LEVELS = [
  { value: 'normal', label: '通常' },
  { value: 'sensitive_reason', label: '理由が機微 (理由は伏せる)' },
  { value: 'sensitive_existence', label: '取得の事実自体が機微' },
] as const;
const DAY_TREATMENTS = [
  { value: 'leave', label: '休暇として扱う' }, { value: 'scheduled_holiday', label: '所定休日として扱う' },
] as const;
const ANNUAL_CAP_PERIOD_BASES = [
  { value: 'fiscal_year', label: '年度' }, { value: 'hire_anniversary', label: '入社応当日' },
  { value: 'calendar_year', label: '暦年' },
] as const;

const labelOf = (options: readonly { value: string; label: string }[], value: string | null): string =>
  options.find((o) => o.value === value)?.label ?? value ?? '—';

/** サーバーがロックしうる9列 (lib/leave/validate.ts CATALOG_LOCKED_FIELD_MAP)。表示名はフォームと揃える。 */
const LOCKABLE_FIELD_LABELS: Record<string, string> = {
  balance_model: '残高の管理方式', allowed_units: '取得単位', accrual_logic: '付与ロジック',
  pay_treatment: '賃金の取扱い', expiry_model: '時効の扱い', sensitivity_level: '機微度',
  attendance_rate_treatment: '出勤率の扱い', requires_family_target: '対象家族の指定',
  is_period_type: '期間型 (長期休業)',
};

/** 法定最低基準チェック (§4-2) の警告文。保存はブロックしない (分類B)。 */
function MinimumRuleFindings({ findings }: { findings: MinimumRuleFinding[] }) {
  if (findings.length === 0) return null;
  return (
    <ul className="mt-1 space-y-1">
      {findings.map((f, i) => (
        <li key={`${f.kind}-${f.field}-${f.constantKey}-${i}`} className="flex items-start gap-1 text-xs text-alert-text">
          <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
          {f.kind === 'unconfirmed' ? (
            <span>
              {LOCKABLE_FIELD_LABELS[f.field] ?? f.field} の法定最低基準 ({f.constantKey}) が未確認のため、
              基準を満たしているか判定できません。下の「法定値の確認状況」から確認してください。
            </span>
          ) : (
            <span>
              {LOCKABLE_FIELD_LABELS[f.field] ?? f.field} が法定最低基準を下回っています
              (設定値: {f.actual === null ? '未設定' : f.actual} / 法定最低: {f.floor})。
              出典: {f.sourceCitation || '—'}、最終確認日: {f.lastVerifiedOn || '—'}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** D-93: 法定値は必ず「値 + 説明 + 出典 + 最終確認日」を併記する。値の形は定数ごとに異なる。 */
const formatLegalValue = (v: LegalValue): string => {
  const value = v.value;
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'object') return `${value}${v.unit ? ` ${v.unit}` : ''}`;
  if (Array.isArray(value)) return JSON.stringify(value);
  const obj = value as Record<string, unknown>;
  const entries = Object.entries(obj);
  if (entries.length === 1 && typeof entries[0]![1] !== 'object') {
    return `${entries[0]![1]}${v.unit ? ` ${v.unit}` : ''}`;
  }
  return JSON.stringify(value);
};

// -----------------------------------------------------------------------------
// 法定値の確認状況 (§6-3⑤ / D-279)
// -----------------------------------------------------------------------------
function LegalValuesSection({ canConfirm }: { canConfirm: boolean }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, isError } = useQuery<LegalValue[]>({
    queryKey: ['leave-legal-values'], queryFn: () => apiFetch<LegalValue[]>('/leave-types/legal-values'),
  });

  const confirmMut = useMutation({
    mutationFn: (key: string) => apiFetch(`/leave-types/legal-values/${encodeURIComponent(key)}/confirm`, {
      method: 'POST', body: JSON.stringify({}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-legal-values'] });
      // 未確認が解消すると minimum_rule_findings も変わる
      qc.invalidateQueries({ queryKey: ['leave-types'] });
      toast.success('確認しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確認に失敗しました'),
  });

  const values = data ?? [];
  const pending = values.filter((v) => v.status !== 'confirmed');
  const shown = expanded ? values : pending;

  return (
    <section className="border border-surface-border rounded-lg bg-surface-card p-3 space-y-2">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-text-body">法定値の確認状況</h3>
          {isLoading ? (
            <p role="status" className="text-sm text-text-sub mt-1">読み込み中…</p>
          ) : isError ? (
            <p role="alert" className="text-sm text-alert-text mt-1">法定値の取得に失敗しました。再読み込みしてください。</p>
          ) : !data ? (
            <p role="status" className="text-sm text-text-sub mt-1">法定値を取得できていません。再読み込みしてください。</p>
          ) : pending.length === 0 ? (
            <p className="text-sm text-success-text mt-1 flex items-center gap-1">
              <CheckCircle2 size={14} className="shrink-0" aria-hidden />
              法定値はすべて確認済みです ({values.length}件)
            </p>
          ) : (
            <p className="text-sm text-alert-text mt-1 flex items-start gap-1">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
              <span>
                法定値の未確認 {pending.length} 件 (全{values.length}件)。
                <strong className="font-semibold">未確認の法定値がある間は、その値を使う自動付与が実行されません。</strong>
                内容を確認して「確認する」を押してください。
              </span>
            </p>
          )}
        </div>
        {values.length > 0 && (
          <Button variant="ghost" size="sm" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
            {expanded ? '未確認のみ表示' : `すべて表示 (${values.length}件)`}
          </Button>
        )}
      </div>

      {shown.length > 0 && (
        <ul className="space-y-2">
          {shown.map((v) => (
            <li key={v.constant_key} className="border border-surface-line rounded p-2 bg-surface-canvas">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-text-body break-words">
                    {v.label}
                    {v.status === 'stale' && (
                      <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">
                        確認後に法定値が変わりました
                      </span>
                    )}
                    {v.status === 'unconfirmed' && (
                      <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">
                        未確認
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-text-body mt-0.5">
                    値: <span className="font-semibold">{formatLegalValue(v)}</span>
                  </p>
                  {v.explanation && <p className="text-xs text-text-sub mt-0.5 break-words">{v.explanation}</p>}
                  <p className="text-xs text-text-sub mt-0.5">
                    出典: {v.source_citation || '—'}、最終確認日: {v.last_verified_on || '—'}
                    {v.confirmed_at && `、テナント確認日時: ${v.confirmed_at}`}
                  </p>
                </div>
                {/* ⚠️ 押しても必ず403になるボタンを出さない (F-4/D-209 と同型) */}
                {canConfirm && v.status !== 'confirmed' && (
                  <Button variant="secondary" size="sm" loading={confirmMut.isPending}
                    onClick={() => confirmMut.mutate(v.constant_key)}>
                    確認する
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// 慶弔の事由×続柄 (leave_type_event_rules)
// ⚠️ EntriesPanel (StandingPatternTemplatesSettingsPage) と同じく、このパネル自体は Modal に
//    しない。行の編集も同一パネル内のインライン編集で完結させ Modal の入れ子を作らない。
// -----------------------------------------------------------------------------
type EventRuleDraft = { event_code: string; event_label: string; relationship_code: string; granted_days: string; is_paid: 'inherit' | 'paid' | 'unpaid' };

const toDraft = (r: EventRule): EventRuleDraft => ({
  event_code: r.event_code, event_label: r.event_label,
  relationship_code: r.relationship_code ?? NO_RELATIONSHIP_VALUE,
  granted_days: String(r.granted_days),
  is_paid: r.is_paid === null ? 'inherit' : r.is_paid ? 'paid' : 'unpaid',
});

function EventRulesPanel({ leaveType, canManage }: { leaveType: LeaveType; canManage: boolean }) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<EventRuleDraft[] | null>(null);

  const { data, isLoading, isError } = useQuery<EventRule[]>({
    queryKey: ['leave-type-event-rules', leaveType.leave_type_id],
    queryFn: () => apiFetch<EventRule[]>(`/leave-types/${leaveType.leave_type_id}/event-rules`),
  });
  // 続柄カタログは family_member_management フラグに依存するため、取得できない場合もある。
  // 取得できないときは続柄を「指定しない」だけに縮退させ、パネル自体は使えるままにする。
  const { data: relData } = useQuery<{ relationships: FamilyRelationship[] }>({
    queryKey: ['family-relationships'],
    queryFn: () => apiFetch<{ relationships: FamilyRelationship[] }>('/family-relationships'),
    retry: false,
  });
  const relationships = relData?.relationships ?? [];
  const relLabelByCode = useMemo(() => new Map(relationships.map((r) => [r.relationship_code, r.label])), [relationships]);

  const rules = data ?? [];
  const editing = drafts ?? rules.map(toDraft);

  const saveMut = useMutation({
    mutationFn: (next: EventRuleDraft[]) => apiFetch(`/leave-types/${leaveType.leave_type_id}/event-rules`, {
      method: 'PUT',
      body: JSON.stringify({
        rules: next.map((d) => ({
          event_code: d.event_code.trim(), event_label: d.event_label.trim(),
          relationship_code: d.relationship_code === NO_RELATIONSHIP_VALUE ? null : d.relationship_code,
          granted_days: Number(d.granted_days) || 0,
          is_paid: d.is_paid === 'inherit' ? null : d.is_paid === 'paid',
        })),
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-type-event-rules', leaveType.leave_type_id] });
      setDrafts(null);
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const update = (index: number, patch: Partial<EventRuleDraft>) =>
    setDrafts(editing.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  // サーバーは (event_code, relationship_code) の重複を 400 で弾く。押す前に警告する。
  const duplicateIndexes = useMemo(() => {
    const seen = new Map<string, number>();
    const dup = new Set<number>();
    editing.forEach((d, i) => {
      const key = `${d.event_code.trim()}::${d.relationship_code === NO_RELATIONSHIP_VALUE ? '' : d.relationship_code}`;
      if (d.event_code.trim() === '') return;
      const first = seen.get(key);
      if (first !== undefined) { dup.add(first); dup.add(i); } else seen.set(key, i);
    });
    return dup;
  }, [editing]);

  const hasBlankRequired = editing.some((d) => d.event_code.trim() === '' || d.event_label.trim() === '');
  const dirty = drafts !== null;
  const canSave = canManage && dirty && duplicateIndexes.size === 0 && !hasBlankRequired && editing.length <= 200;

  return (
    <div className="mt-2 pt-2 border-t border-surface-line space-y-2">
      <p className="text-xs text-text-sub">
        慶弔休暇のように「事由ごと・続柄ごとに日数が変わる」種別で使います。続柄を指定しない行は、その事由の既定として扱われます。
      </p>
      {isLoading ? (
        <div role="status" className="text-sm text-text-sub">読み込み中…</div>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">事由の設定を取得できませんでした。再読み込みしてください。</p>
      ) : (
        <>
          {editing.length === 0 ? (
            <p className="text-sm text-text-sub">事由がまだ登録されていません。</p>
          ) : (
            <ul className="space-y-2">
              {editing.map((d, i) => (
                <li key={i} className={`border rounded p-2 ${duplicateIndexes.has(i) ? 'border-alert-border bg-alert-bg' : 'border-surface-line'}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-text-sub mb-1" htmlFor={`ev-code-${leaveType.leave_type_id}-${i}`}>事由コード</label>
                      <input id={`ev-code-${leaveType.leave_type_id}-${i}`} value={d.event_code} disabled={!canManage} maxLength={50}
                        onChange={(e) => update(i, { event_code: e.target.value })} className={inputCls} placeholder="例: bereavement" />
                    </div>
                    <div>
                      <label className="block text-xs text-text-sub mb-1" htmlFor={`ev-label-${leaveType.leave_type_id}-${i}`}>事由の名称</label>
                      <input id={`ev-label-${leaveType.leave_type_id}-${i}`} value={d.event_label} disabled={!canManage} maxLength={200}
                        onChange={(e) => update(i, { event_label: e.target.value })} className={inputCls} placeholder="例: 忌引" />
                    </div>
                    <div>
                      <label className="block text-xs text-text-sub mb-1">続柄</label>
                      <Select value={d.relationship_code} onValueChange={(v) => update(i, { relationship_code: v })} disabled={!canManage}>
                        <SelectTrigger className="w-full min-h-[44px]" aria-label="続柄"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_RELATIONSHIP_VALUE}>指定しない (事由の既定)</SelectItem>
                          {relationships.map((r) => (
                            <SelectItem key={r.relationship_code} value={r.relationship_code}>{r.label}</SelectItem>
                          ))}
                          {/* 続柄カタログが取得できない場合でも既存値は失わない */}
                          {d.relationship_code !== NO_RELATIONSHIP_VALUE && !relLabelByCode.has(d.relationship_code) && (
                            <SelectItem value={d.relationship_code}>{d.relationship_code}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-text-sub mb-1" htmlFor={`ev-days-${leaveType.leave_type_id}-${i}`}>付与日数</label>
                        <input id={`ev-days-${leaveType.leave_type_id}-${i}`} type="number" min={0} step="0.5" value={d.granted_days}
                          disabled={!canManage} onChange={(e) => update(i, { granted_days: e.target.value })} className={numberInputCls} />
                      </div>
                      <div>
                        <label className="block text-xs text-text-sub mb-1">賃金</label>
                        <Select value={d.is_paid} onValueChange={(v) => update(i, { is_paid: v as EventRuleDraft['is_paid'] })} disabled={!canManage}>
                          <SelectTrigger className="w-full min-h-[44px]" aria-label="賃金の取扱い"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">種別の設定に従う</SelectItem>
                            <SelectItem value="paid">有給</SelectItem>
                            <SelectItem value="unpaid">無給</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  {duplicateIndexes.has(i) && (
                    <p role="alert" className="text-xs text-alert-text mt-1 flex items-start gap-1">
                      <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
                      事由コードと続柄の組合せが他の行と重複しています。保存できません。
                    </p>
                  )}
                  {canManage && (
                    <div className="flex justify-end mt-1">
                      <button type="button" aria-label={`${i + 1}行目を削除`}
                        onClick={() => setDrafts(editing.filter((_, idx) => idx !== i))}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {canManage && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} disabled={editing.length >= 200}
                onClick={() => setDrafts([...editing, { event_code: '', event_label: '', relationship_code: NO_RELATIONSHIP_VALUE, granted_days: '0', is_paid: 'inherit' }])}>
                事由を追加
              </Button>
              {dirty && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setDrafts(null)} disabled={saveMut.isPending}>変更を破棄</Button>
                  <Button variant="primary" size="sm" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending}
                    onClick={() => saveMut.mutate(editing)}>
                    事由の設定を保存
                  </Button>
                </>
              )}
              {editing.length >= 200 && <span className="text-xs text-alert-text">事由は200件までです。</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 種別の編集フォーム
// -----------------------------------------------------------------------------
type Draft = {
  leave_type_id: string | null;
  code: string; label: string; statutory_kind: string;
  day_treatment: string; balance_model: string; allowed_units: string[];
  accrual_logic: string; pay_treatment: string; pay_calc_method: string;
  expiry_model: string; expiry_years: string;
  attendance_rate_treatment: string; sensitivity_level: string;
  approval_usage_point_id: string;
  requires_family_target: boolean; is_period_type: boolean;
  annual_cap_days: string; annual_cap_days_multi: string; annual_cap_period_basis: string;
  per_event_cap_days: string; per_target_cap_days: string; per_target_max_splits: string;
  hourly_annual_cap_days: string; tenure_rules_json: string;
  carryover_source_leave_type_id: string; carryover_max_days: string;
  requires_event_code: boolean; is_active: boolean; sort_order: string;
};

const emptyDraft = (sortOrder: number): Draft => ({
  leave_type_id: null, code: '', label: '', statutory_kind: NO_STATUTORY_VALUE,
  day_treatment: 'leave', balance_model: 'granted', allowed_units: ['day'],
  accrual_logic: 'fixed_annual', pay_treatment: 'unpaid', pay_calc_method: NO_STATUTORY_VALUE,
  expiry_model: 'expire_at_year_end', expiry_years: '',
  attendance_rate_treatment: 'excluded_from_denominator', sensitivity_level: 'normal',
  approval_usage_point_id: NO_APPROVAL_VALUE,
  requires_family_target: false, is_period_type: false,
  annual_cap_days: '', annual_cap_days_multi: '', annual_cap_period_basis: NO_STATUTORY_VALUE,
  per_event_cap_days: '', per_target_cap_days: '', per_target_max_splits: '',
  hourly_annual_cap_days: '', tenure_rules_json: '',
  carryover_source_leave_type_id: NO_STATUTORY_VALUE, carryover_max_days: '',
  requires_event_code: false, is_active: true, sort_order: String(sortOrder),
});

const toEditDraft = (t: LeaveType): Draft => ({
  leave_type_id: t.leave_type_id, code: t.code, label: t.label,
  statutory_kind: t.statutory_kind ?? NO_STATUTORY_VALUE,
  day_treatment: t.day_treatment, balance_model: t.balance_model, allowed_units: t.allowed_units,
  accrual_logic: t.accrual_logic, pay_treatment: t.pay_treatment,
  pay_calc_method: t.pay_calc_method ?? NO_STATUTORY_VALUE,
  expiry_model: t.expiry_model, expiry_years: t.expiry_years === null ? '' : String(t.expiry_years),
  attendance_rate_treatment: t.attendance_rate_treatment, sensitivity_level: t.sensitivity_level,
  approval_usage_point_id: t.approval_usage_point_id ?? NO_APPROVAL_VALUE,
  requires_family_target: t.requires_family_target, is_period_type: t.is_period_type,
  annual_cap_days: t.annual_cap_days === null ? '' : String(t.annual_cap_days),
  annual_cap_days_multi: t.annual_cap_days_multi === null ? '' : String(t.annual_cap_days_multi),
  annual_cap_period_basis: t.annual_cap_period_basis ?? NO_STATUTORY_VALUE,
  per_event_cap_days: t.per_event_cap_days === null ? '' : String(t.per_event_cap_days),
  per_target_cap_days: t.per_target_cap_days === null ? '' : String(t.per_target_cap_days),
  per_target_max_splits: t.per_target_max_splits === null ? '' : String(t.per_target_max_splits),
  hourly_annual_cap_days: t.hourly_annual_cap_days === null ? '' : String(t.hourly_annual_cap_days),
  tenure_rules_json: t.tenure_rules_json ?? '',
  carryover_source_leave_type_id: t.carryover_source_leave_type_id ?? NO_STATUTORY_VALUE,
  carryover_max_days: t.carryover_max_days === null ? '' : String(t.carryover_max_days),
  requires_event_code: t.requires_event_code, is_active: t.is_active, sort_order: String(t.sort_order),
});

const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));

function FormSection({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <div>
        <h4 className="text-sm font-semibold text-text-body">{title}</h4>
        {description && <p className="text-xs text-text-sub mt-0.5">{description}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** 法定でロックされている列は入力させず、カタログ値を表示するだけにする。 */
function LockedField({ label, value, citation }: { label: string; value: string; citation: string }) {
  return (
    <div>
      <span className="block text-sm text-text-sub mb-1">{label}</span>
      <div className="min-h-[44px] px-3 py-2 rounded border border-surface-border bg-surface-sunken text-sm text-text-body flex items-center flex-wrap gap-x-2">
        <span className="font-semibold">{value}</span>
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-card text-text-sub border border-surface-border whitespace-nowrap">法定で固定</span>
      </div>
      {citation && <p className="text-xs text-text-sub mt-1">根拠: {citation}</p>}
    </div>
  );
}

function LeaveTypeFormModal({
  draft, setDraft, catalog, usagePoints, canSelectUsagePoint, leaveTypes, saving, onClose, onSave,
}: {
  draft: Draft; setDraft: (d: Draft) => void; catalog: CatalogEntry[];
  usagePoints: ApprovalUsagePoint[]; canSelectUsagePoint: boolean; leaveTypes: LeaveType[];
  saving: boolean; onClose: () => void; onSave: () => void;
}) {
  const statutoryKind = draft.statutory_kind === NO_STATUTORY_VALUE ? null : draft.statutory_kind;
  const catalogEntry = statutoryKind ? catalog.find((c) => c.statutory_kind === statutoryKind) : undefined;
  const overridable = useMemo(
    () => new Set(catalogEntry?.tenant_overridable_fields ?? []),
    [catalogEntry],
  );
  // 法定行かつ tenant_overridable_fields に無い列はサーバーがカタログ値で強制する
  const isLocked = (field: string) => !!catalogEntry && !overridable.has(field);
  const isMandatory = catalogEntry?.is_mandatory === true;
  const canSave = draft.code.trim() !== '' && draft.label.trim() !== '' && draft.allowed_units.length > 0;

  const toggleUnit = (unit: string) => {
    const next = draft.allowed_units.includes(unit)
      ? draft.allowed_units.filter((u) => u !== unit)
      : [...draft.allowed_units, unit];
    setDraft({ ...draft, allowed_units: next });
  };

  // 法定種別を選び直したらカタログ既定値を取り込む (ロック列は保存時にどのみち上書きされる)
  const applyStatutoryKind = (value: string) => {
    const entry = value === NO_STATUTORY_VALUE ? undefined : catalog.find((c) => c.statutory_kind === value);
    if (!entry) { setDraft({ ...draft, statutory_kind: value }); return; }
    setDraft({
      ...draft, statutory_kind: value,
      label: draft.label.trim() === '' ? entry.label : draft.label,
      balance_model: entry.balance_model, allowed_units: entry.allowed_units,
      accrual_logic: entry.accrual_logic, pay_treatment: entry.default_pay_treatment,
      expiry_model: entry.expiry_model, sensitivity_level: entry.sensitivity_level,
      attendance_rate_treatment: entry.attendance_rate_treatment,
      requires_family_target: entry.requires_family_target, is_period_type: entry.is_period_type,
    });
  };

  const citation = catalogEntry?.statute_citation ?? '';

  return (
    <Modal open title={draft.leave_type_id ? '休暇種別を編集' : '休暇種別を追加'} onClose={onClose} size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saving} loading={saving} onClick={onSave}>保存</Button>
        </>
      )}>
      <div className="space-y-6">
        <FormSection title="基本" description="どの休暇かを識別する情報です。">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-code">コード</label>
              <input id="lt-code" value={draft.code} maxLength={50} className={inputCls} placeholder="例: annual_paid"
                onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-label">名称</label>
              <input id="lt-label" value={draft.label} maxLength={200} className={inputCls} placeholder="例: 年次有給休暇"
                onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">法定制度</label>
            <Select value={draft.statutory_kind} onValueChange={applyStatutoryKind} disabled={isMandatory}>
              <SelectTrigger className="w-full min-h-[44px]" aria-label="法定制度"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_STATUTORY_VALUE}>法定外 (会社独自の休暇)</SelectItem>
                {catalog.map((c) => (
                  <SelectItem key={c.statutory_kind} value={c.statutory_kind}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isMandatory && (
              <p className="text-xs text-text-sub mt-1">
                法定義務の休暇のため、法定制度の変更・削除はできません。
              </p>
            )}
            {catalogEntry && (
              <p className="text-xs text-text-sub mt-1">
                根拠: {citation || '—'}。
                {overridable.size === 0
                  ? '取扱いはすべて法定で固定され、変更できません。'
                  : `テナントが変更できるのは ${[...overridable].map((f) => LOCKABLE_FIELD_LABELS[f] ?? f).join('・')} のみです。`}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">暦日の扱い</label>
              <Select value={draft.day_treatment} onValueChange={(v) => setDraft({ ...draft, day_treatment: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="暦日の扱い"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAY_TREATMENTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-sort">並び順</label>
              <input id="lt-sort" type="number" value={draft.sort_order} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="w-5 h-5" checked={draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
            有効 (申請で選べる)
          </label>
        </FormSection>

        <FormSection title="残高・付与" description="どのように日数を数え、どのように付与するかです。">
          {isLocked('balance_model') ? (
            <LockedField label="残高の管理方式" value={labelOf(BALANCE_MODELS, catalogEntry!.balance_model)} citation={citation} />
          ) : (
            <div>
              <label className="block text-sm text-text-sub mb-1">残高の管理方式</label>
              <Select value={draft.balance_model} onValueChange={(v) => setDraft({ ...draft, balance_model: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="残高の管理方式"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BALANCE_MODELS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLocked('allowed_units') ? (
            <LockedField label="取得単位" value={catalogEntry!.allowed_units.map((u) => labelOf(UNITS, u)).join('・')} citation={citation} />
          ) : (
            <fieldset>
              <legend className="block text-sm text-text-sub mb-1">取得単位 (1つ以上)</legend>
              <div className="flex flex-wrap gap-x-4">
                {UNITS.map((u) => (
                  <label key={u.value} className="flex items-center gap-2 text-sm min-h-[44px]">
                    <input type="checkbox" className="w-5 h-5" checked={draft.allowed_units.includes(u.value)}
                      onChange={() => toggleUnit(u.value)} />
                    {u.label}
                  </label>
                ))}
              </div>
              {draft.allowed_units.length === 0 && (
                <p role="alert" className="text-xs text-alert-text">取得単位を1つ以上選んでください。</p>
              )}
            </fieldset>
          )}

          {isLocked('accrual_logic') ? (
            <LockedField label="付与ロジック" value={labelOf(ACCRUAL_LOGICS, catalogEntry!.accrual_logic)} citation={citation} />
          ) : (
            <div>
              <label className="block text-sm text-text-sub mb-1">付与ロジック</label>
              <Select value={draft.accrual_logic} onValueChange={(v) => setDraft({ ...draft, accrual_logic: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="付与ロジック"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCRUAL_LOGICS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLocked('pay_treatment') ? (
            <LockedField label="賃金の取扱い" value={labelOf(PAY_TREATMENTS, catalogEntry!.default_pay_treatment)} citation={citation} />
          ) : (
            <div>
              <label className="block text-sm text-text-sub mb-1">賃金の取扱い</label>
              <Select value={draft.pay_treatment} onValueChange={(v) => setDraft({ ...draft, pay_treatment: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="賃金の取扱い"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAY_TREATMENTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="block text-sm text-text-sub mb-1">賃金の算定方法</label>
            <Select value={draft.pay_calc_method} onValueChange={(v) => setDraft({ ...draft, pay_calc_method: v })}>
              <SelectTrigger className="w-full min-h-[44px]" aria-label="賃金の算定方法"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_STATUTORY_VALUE}>指定しない</SelectItem>
                {PAY_CALC_METHODS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isLocked('expiry_model') ? (
            <LockedField label="時効の扱い" value={labelOf(EXPIRY_MODELS, catalogEntry!.expiry_model)} citation={citation} />
          ) : (
            <div>
              <label className="block text-sm text-text-sub mb-1">時効の扱い</label>
              <Select value={draft.expiry_model} onValueChange={(v) => setDraft({ ...draft, expiry_model: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="時効の扱い"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPIRY_MODELS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-expiry-years">時効までの年数</label>
              <input id="lt-expiry-years" type="number" min={0} value={draft.expiry_years} className={numberInputCls}
                placeholder="例: 2" onChange={(e) => setDraft({ ...draft, expiry_years: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">積立元の休暇種別</label>
              <Select value={draft.carryover_source_leave_type_id} onValueChange={(v) => setDraft({ ...draft, carryover_source_leave_type_id: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="積立元の休暇種別"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_STATUTORY_VALUE}>指定しない</SelectItem>
                  {leaveTypes.filter((t) => t.leave_type_id !== draft.leave_type_id).map((t) => (
                    <SelectItem key={t.leave_type_id} value={t.leave_type_id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-text-sub mt-1">時効で消滅する日数をこの種別へ積み立てる場合に指定します。</p>
            </div>
          </div>
        </FormSection>

        <FormSection title="上限" description="使わない項目は空欄のままにしてください (上限なしとして扱われます)。">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-annual-cap">年間上限 (日)</label>
              <input id="lt-annual-cap" type="number" min={0} step="0.5" value={draft.annual_cap_days} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, annual_cap_days: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-annual-cap-multi">年間上限 (対象が2人以上、日)</label>
              <input id="lt-annual-cap-multi" type="number" min={0} step="0.5" value={draft.annual_cap_days_multi} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, annual_cap_days_multi: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">年間上限の起算</label>
              <Select value={draft.annual_cap_period_basis} onValueChange={(v) => setDraft({ ...draft, annual_cap_period_basis: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="年間上限の起算"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_STATUTORY_VALUE}>指定しない</SelectItem>
                  {ANNUAL_CAP_PERIOD_BASES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-hourly-cap">時間単位取得の年間上限 (日)</label>
              <input id="lt-hourly-cap" type="number" min={0} step="0.5" value={draft.hourly_annual_cap_days} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, hourly_annual_cap_days: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-event-cap">事由ごとの上限 (日)</label>
              <input id="lt-event-cap" type="number" min={0} step="0.5" value={draft.per_event_cap_days} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, per_event_cap_days: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-target-cap">対象者ごとの上限 (日)</label>
              <input id="lt-target-cap" type="number" min={0} step="0.5" value={draft.per_target_cap_days} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, per_target_cap_days: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-target-splits">対象者ごとの分割回数の上限</label>
              <input id="lt-target-splits" type="number" min={0} value={draft.per_target_max_splits} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, per_target_max_splits: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="lt-carryover-max">繰越の上限 (日)</label>
              <input id="lt-carryover-max" type="number" min={0} step="0.5" value={draft.carryover_max_days} className={numberInputCls}
                onChange={(e) => setDraft({ ...draft, carryover_max_days: e.target.value })} />
            </div>
          </div>
        </FormSection>

        <FormSection title="その他" description="出勤率の扱い・機微度・申請時の入力項目です。">
          {isLocked('attendance_rate_treatment') ? (
            <LockedField label="出勤率の扱い" value={labelOf(ATTENDANCE_TREATMENTS, catalogEntry!.attendance_rate_treatment)} citation={citation} />
          ) : (
            <div>
              <label className="block text-sm text-text-sub mb-1">出勤率の扱い</label>
              <Select value={draft.attendance_rate_treatment} onValueChange={(v) => setDraft({ ...draft, attendance_rate_treatment: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="出勤率の扱い"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ATTENDANCE_TREATMENTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLocked('sensitivity_level') ? (
            <LockedField label="機微度" value={labelOf(SENSITIVITY_LEVELS, catalogEntry!.sensitivity_level)} citation={citation} />
          ) : (
            <div>
              <label className="block text-sm text-text-sub mb-1">機微度</label>
              <Select value={draft.sensitivity_level} onValueChange={(v) => setDraft({ ...draft, sensitivity_level: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="機微度"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SENSITIVITY_LEVELS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLocked('requires_family_target') ? (
            <LockedField label="対象家族の指定" value={catalogEntry!.requires_family_target ? '必要' : '不要'} citation={citation} />
          ) : (
            <label className="flex items-center gap-2 text-sm min-h-[44px]">
              <input type="checkbox" className="w-5 h-5" checked={draft.requires_family_target}
                onChange={(e) => setDraft({ ...draft, requires_family_target: e.target.checked })} />
              申請時に対象の家族を指定する
            </label>
          )}

          {isLocked('is_period_type') ? (
            <LockedField label="期間型 (長期休業)" value={catalogEntry!.is_period_type ? 'はい' : 'いいえ'} citation={citation} />
          ) : (
            <label className="flex items-center gap-2 text-sm min-h-[44px]">
              <input type="checkbox" className="w-5 h-5" checked={draft.is_period_type}
                onChange={(e) => setDraft({ ...draft, is_period_type: e.target.checked })} />
              期間型 (産休・育休のように開始日と終了日で管理する)
            </label>
          )}

          <label className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="w-5 h-5" checked={draft.requires_event_code}
              onChange={(e) => setDraft({ ...draft, requires_event_code: e.target.checked })} />
            申請時に事由を選ばせる (慶弔休暇など)
          </label>

          <div>
            <label className="block text-sm text-text-sub mb-1">承認ワークフローの利用箇所</label>
            {canSelectUsagePoint ? (
              <Select value={draft.approval_usage_point_id} onValueChange={(v) => setDraft({ ...draft, approval_usage_point_id: v })}>
                <SelectTrigger className="w-full min-h-[44px]" aria-label="承認ワークフローの利用箇所"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_APPROVAL_VALUE}>使わない (届出のみ)</SelectItem>
                  {usagePoints.map((u) => (
                    <SelectItem key={u.usage_point_id} value={u.usage_point_id}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-text-sub">
                承認ワークフロー設定を閲覧する権限がないため変更できません
                {draft.approval_usage_point_id !== NO_APPROVAL_VALUE && ` (現在の設定: ${draft.approval_usage_point_id})`}。
              </p>
            )}
            <p className="text-xs text-text-sub mt-1">
              年次有給休暇のように会社に拒否権が無い休暇には割り当てられません (時季指定制のため)。
            </p>
          </div>

          <div>
            <label className="block text-sm text-text-sub mb-1" htmlFor="lt-tenure">勤続年数別の付与ルール (JSON)</label>
            <textarea id="lt-tenure" value={draft.tenure_rules_json} rows={3}
              className={`${inputCls} font-mono text-xs`} placeholder='例: [{"years":1,"days":10}]'
              onChange={(e) => setDraft({ ...draft, tenure_rules_json: e.target.value })} />
            <p className="text-xs text-text-sub mt-1">付与ロジックが「勤続年数に応じる」のときだけ使われます。</p>
          </div>
        </FormSection>
      </div>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
export function LeaveTypesSettingsPage() {
  const t = useLabel();
  const user = useAuthStore((s) => s.user);
  const perms = user?.effective_permissions;
  const nonInheriting = user?.non_inheriting_features;
  const permsReady = usePermsReady();
  const canView = hasPermission(perms, 'leave_type', 'view', nonInheriting);
  // ⚠️ TD-22: 休暇種別の CRUD は requireTenantWideManage (routes/leave-types.ts) で
  // 全店舗スコープを要求する。UI 側でも同じ条件を再現する。
  const canManage = hasTenantWidePermission(perms, 'leave_type', 'manage', nonInheriting);
  const canViewLegal = hasPermission(perms, 'leave_grant', 'view', nonInheriting);
  const canConfirmLegal = hasTenantWidePermission(perms, 'leave_grant', 'manage', nonInheriting);
  const canSelectUsagePoint = hasPermission(perms, 'approval_workflow', 'view', nonInheriting);

  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeaveType | null>(null);
  const [eventRulesFor, setEventRulesFor] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<LeaveType[]>({
    queryKey: ['leave-types'], queryFn: () => apiFetch<LeaveType[]>('/leave-types'), enabled: canView,
  });
  const leaveTypes = useMemo(() => data ?? [], [data]);

  const { data: catalogData } = useQuery<CatalogResponse>({
    queryKey: ['leave-types-catalog'], queryFn: () => apiFetch<CatalogResponse>('/leave-types/catalog'), enabled: canView,
  });
  const catalog = useMemo(() => catalogData?.catalog ?? [], [catalogData]);
  const catalogByKind = useMemo(() => new Map(catalog.map((c) => [c.statutory_kind, c])), [catalog]);
  const missingMandatory = catalogData?.missing_mandatory_kinds ?? [];

  const { data: usagePointsData } = useQuery<ApprovalUsagePoint[]>({
    queryKey: ['approval-usage-points'],
    queryFn: () => apiFetch<ApprovalUsagePoint[]>('/approvals/usage-points'),
    enabled: canSelectUsagePoint,
  });
  const usagePoints = usagePointsData ?? [];

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const payload = {
        code: d.code.trim(), label: d.label.trim(),
        statutory_kind: d.statutory_kind === NO_STATUTORY_VALUE ? null : d.statutory_kind,
        day_treatment: d.day_treatment, balance_model: d.balance_model, allowed_units: d.allowed_units,
        accrual_logic: d.accrual_logic, pay_treatment: d.pay_treatment,
        pay_calc_method: d.pay_calc_method === NO_STATUTORY_VALUE ? null : d.pay_calc_method,
        expiry_model: d.expiry_model, expiry_years: numOrNull(d.expiry_years),
        attendance_rate_treatment: d.attendance_rate_treatment, sensitivity_level: d.sensitivity_level,
        approval_usage_point_id: d.approval_usage_point_id === NO_APPROVAL_VALUE ? null : d.approval_usage_point_id,
        requires_family_target: d.requires_family_target, is_period_type: d.is_period_type,
        annual_cap_days: numOrNull(d.annual_cap_days), annual_cap_days_multi: numOrNull(d.annual_cap_days_multi),
        annual_cap_period_basis: d.annual_cap_period_basis === NO_STATUTORY_VALUE ? null : d.annual_cap_period_basis,
        per_event_cap_days: numOrNull(d.per_event_cap_days), per_target_cap_days: numOrNull(d.per_target_cap_days),
        per_target_max_splits: numOrNull(d.per_target_max_splits),
        hourly_annual_cap_days: numOrNull(d.hourly_annual_cap_days),
        tenure_rules_json: d.tenure_rules_json.trim() === '' ? null : d.tenure_rules_json.trim(),
        carryover_source_leave_type_id: d.carryover_source_leave_type_id === NO_STATUTORY_VALUE ? null : d.carryover_source_leave_type_id,
        carryover_max_days: numOrNull(d.carryover_max_days),
        requires_event_code: d.requires_event_code, is_active: d.is_active,
        sort_order: Number(d.sort_order) || 0,
      };
      return d.leave_type_id
        ? apiFetch(`/leave-types/${d.leave_type_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/leave-types', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-types'] });
      qc.invalidateQueries({ queryKey: ['leave-types-catalog'] });
      setDraft(null);
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/leave-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-types'] });
      qc.invalidateQueries({ queryKey: ['leave-types-catalog'] });
      setDeleteTarget(null);
      toast.success('削除しました');
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const startCreate = (statutoryKind?: string) => {
    const base = emptyDraft(leaveTypes.length);
    if (!statutoryKind) { setDraft(base); return; }
    const entry = catalogByKind.get(statutoryKind);
    setDraft(entry ? {
      ...base, statutory_kind: statutoryKind, code: statutoryKind, label: entry.label,
      balance_model: entry.balance_model, allowed_units: entry.allowed_units,
      accrual_logic: entry.accrual_logic, pay_treatment: entry.default_pay_treatment,
      expiry_model: entry.expiry_model, sensitivity_level: entry.sensitivity_level,
      attendance_rate_treatment: entry.attendance_rate_treatment,
      requires_family_target: entry.requires_family_target, is_period_type: entry.is_period_type,
    } : { ...base, statutory_kind: statutoryKind });
  };

  if (!permsReady) {
    return <p role="status" className="text-sm text-text-sub">読み込み中…</p>;
  }
  if (!canView) {
    return <p role="alert" className="text-sm text-text-sub">この画面を閲覧する権限がありません。</p>;
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-text-body">{t('entity.leave_types', '休暇種別')}の設定</h2>
          <p className="text-sm text-text-sub mt-1">
            年次有給休暇・産前産後休業などの法定制度と、会社独自の休暇を登録します。
            法定制度を選ぶと、法令で決まっている取扱いは自動的に固定されます。
          </p>
        </div>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => startCreate()}>
            種別を追加
          </Button>
        )}
      </div>

      {/* §6-3⑤ (D-279): 恒常表示。0件でも「すべて確認済み」を出す */}
      {canViewLegal ? <LegalValuesSection canConfirm={canConfirmLegal} /> : (
        // ⚠️ §6-3⑤ (D-279) は「法定値が未確認の状態を隠さない」を求めている。法定値の一覧は
        // leave_grant:view が要るため出せないが、**節ごと消すと「自動付与が止まっている」という
        // 状態が leave_type:manage だけの担当者に一切伝わらない** (Design レビュー MEDIUM)。
        // 状態そのものは出せなくても、確認が必要なことと担当者の所在は伝える。
        <section className="border border-surface-border rounded-lg bg-surface-card p-4 space-y-1">
          <h2 className="text-h2">法定値の確認状況</h2>
          <p className="text-sm text-text-sub">
            法定値の確認状況を表示する権限がありません。
            <strong className="font-bold text-text-body">未確認の法定値が残っている間、その値を使う自動付与は実行されません。</strong>
            確認は休暇の付与を管理する権限を持つ担当者が行います。
          </p>
        </section>
      )}

      {/* D-276: is_mandatory な法定制度の未登録警告 */}
      {missingMandatory.length > 0 && (
        <section role="alert" className="border border-alert-border rounded-lg bg-alert-bg p-3">
          <p className="text-sm text-alert-text flex items-start gap-1">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
            <span>
              <strong className="font-semibold">法定義務の休暇が未登録です:</strong>{' '}
              {missingMandatory.map((k) => catalogByKind.get(k)?.label ?? k).join('、')}
            </span>
          </p>
          {canManage && (
            <div className="flex flex-wrap gap-2 mt-2">
              {missingMandatory.map((k) => (
                <Button key={k} variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => startCreate(k)}>
                  {catalogByKind.get(k)?.label ?? k}を登録
                </Button>
              ))}
            </div>
          )}
        </section>
      )}

      {isLoading ? (
        <div role="status" className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">休暇種別の取得に失敗しました。再読み込みしてください。</p>
      ) : leaveTypes.length === 0 ? (
        <EmptyState message="休暇種別がまだ登録されていません"
          description={canManage ? '「種別を追加」から登録するか、上の警告から法定義務の休暇を登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {leaveTypes.map((lt) => {
            const entry = lt.statutory_kind ? catalogByKind.get(lt.statutory_kind) : undefined;
            const isMandatory = entry?.is_mandatory === true;
            return (
              <li key={lt.leave_type_id} className="border border-surface-border rounded-lg bg-surface-card p-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-text-body break-words">{lt.label}</span>
                      {/* ⚠️ code (最大50字) と法定ラベル (最大200字) はユーザー入力のため、
                          whitespace-nowrap のままだと単一バッジの幅超過を flex-wrap が救えず
                          320px で横オーバーフローする。max-w-full + truncate で必ず収める。 */}
                      <span title={lt.code}
                        className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap max-w-full truncate">{lt.code}</span>
                      {lt.statutory_kind && (
                        <span title={entry?.label ?? lt.statutory_kind}
                          className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap max-w-full truncate">
                          法定: {entry?.label ?? lt.statutory_kind}
                        </span>
                      )}
                      {isMandatory && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">法定義務</span>
                      )}
                      {!lt.is_active && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">無効</span>
                      )}
                      {lt.minimum_rule_findings.length > 0 && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">
                          要確認 {lt.minimum_rule_findings.length}件
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-sub mt-1">
                      {labelOf(BALANCE_MODELS, lt.balance_model)} / {labelOf(PAY_TREATMENTS, lt.pay_treatment)} /
                      {' '}取得単位: {lt.allowed_units.map((u) => labelOf(UNITS, u)).join('・')}
                    </p>
                    <MinimumRuleFindings findings={lt.minimum_rule_findings} />
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" leftIcon={<ListTree size={14} />}
                      aria-expanded={eventRulesFor === lt.leave_type_id}
                      onClick={() => setEventRulesFor(eventRulesFor === lt.leave_type_id ? null : lt.leave_type_id)}>
                      {canManage ? '事由を設定' : '事由を確認'}
                    </Button>
                    {canManage && (
                      <>
                        <button type="button" aria-label={`${lt.label}を編集`} onClick={() => setDraft(toEditDraft(lt))}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring">
                          <Pencil size={14} />
                        </button>
                        {/* D-276: 法定義務の種別は削除が必ず拒否されるためボタン自体を出さない */}
                        {!isMandatory && (
                          <button type="button" aria-label={`${lt.label}を削除`} onClick={() => setDeleteTarget(lt)}
                            className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {eventRulesFor === lt.leave_type_id && (
                  <EventRulesPanel leaveType={lt} canManage={canManage} />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {draft && (
        <LeaveTypeFormModal draft={draft} setDraft={setDraft} catalog={catalog} usagePoints={usagePoints}
          canSelectUsagePoint={canSelectUsagePoint} leaveTypes={leaveTypes} saving={saveMut.isPending}
          onClose={() => setDraft(null)} onSave={() => saveMut.mutate(draft)} />
      )}

      <ConfirmDialog open={!!deleteTarget} title="休暇種別を削除"
        message={`「${deleteTarget?.label ?? ''}」を削除します。過去の申請・付与の記録は残りますが、以降この種別では申請できなくなります。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.leave_type_id)} />
    </div>
  );
}
